// MEN-4239: Figma-derived styling tokens for the coach calendar surfaces.
//
// Tailwind preflight is disabled in this app (`tailwind.config.cjs`), so a bare
// `border-l` sets `border-left-width` while `border-left-style` stays `none`
// and the stroke never paints. Every border utility here is paired with
// `border-0 ... border-solid`, the same idiom the rest of `src/` uses, which is
// what makes the calendar grid visible at all.
//
// Measurements come from Figma "Coach - Upcoming calendar view week 2"
// (node 16447:47479): 56px time gutter, 66px day header, 55px hour rows,
// 16px/bold day headings (#A4A4A4 weekday over black date), and session
// blocks filled #BDCCBE with a 10px/bold name over 8px/medium detail lines.
//
// The hour row stays at 56px rather than Figma's 55px: `WeekView` scroll
// restoration (and its tests) are anchored to a 56px row.

export const HOUR_ROW_HEIGHT_PX = 56;

/** Time-label gutter, then one column per weekday. */
export const WEEK_GRID_TEMPLATE_COLUMNS = '3.5rem repeat(7, 1fr)';

export const WEEK_DAY_HEADER_CLASS =
  'relative flex h-[66px] flex-col justify-center border-0 border-l border-solid border-cement-200 px-2 text-center';

/** Today keeps the full sage outline called out in the design. */
export const WEEK_DAY_HEADER_TODAY_CLASS =
  'relative flex h-[66px] flex-col justify-center border border-solid border-sage-500 px-2 text-center';

export const WEEK_DAY_HEADER_WEEKDAY_CLASS =
  'text-[16px] font-bold leading-6 tracking-[0.15px] text-cement-300';

export const WEEK_DAY_HEADER_DATE_CLASS =
  'text-[16px] font-bold leading-6 tracking-[0.15px] text-cement-600';

export const WEEK_HEADER_ROW_CLASS =
  'grid border-0 border-b border-solid border-cement-200';

export const WEEK_ALL_DAY_ROW_CLASS =
  'grid overflow-y-auto border-0 border-b border-solid border-cement-200';

export const WEEK_ALL_DAY_CELL_CLASS =
  'min-h-6 border-0 border-l border-solid border-cement-200 px-1';

export const WEEK_HOUR_LABEL_CLASS = 'text-cement-300';

export const WEEK_HOUR_GUTTER_CELL_CLASS = 'h-14 pr-3 text-right';

export const WEEK_HOUR_ROW_CLASS =
  'h-14 shrink-0 border-0 border-b border-solid border-cement-200';

export const WEEK_DAY_COLUMN_CLASS =
  'relative border-0 border-l border-solid border-cement-200';

/** Session block: square sage fill, 6px inset, vertically centred text. */
export const WEEK_SESSION_BLOCK_CLASS =
  'absolute flex appearance-none flex-col justify-center overflow-hidden border-0 bg-sage-400 px-[6px] text-left tracking-[0.15px] text-cement-600';

export const WEEK_SESSION_NAME_CLASS =
  'block truncate text-[10px] font-bold leading-[15px]';

export const WEEK_SESSION_DETAIL_CLASS =
  'block truncate text-[8px] font-medium leading-3';

/**
 * Unavailable hours read as a fine grey hatch in Figma rather than the
 * heavier slate wash the first pass shipped.
 */
export const UNAVAILABLE_BACKGROUND =
  'repeating-linear-gradient(135deg, rgba(164, 164, 164, 0.32) 0, rgba(164, 164, 164, 0.32) 1px, transparent 1px, transparent 6px)';

/**
 * MEN-5476: Chem Call unavailable hours. Blue and counter-diagonal so it reads
 * apart from the grey coaching hatch; matches the Moments admin calendar.
 */
export const CHEM_UNAVAILABLE_BACKGROUND =
  'repeating-linear-gradient(45deg, rgba(70, 120, 200, 0.30) 0, rgba(70, 120, 200, 0.30) 1px, transparent 1px, transparent 6px)';

export const MONTH_HEADER_ROW_CLASS =
  'grid grid-cols-7 border-0 border-b border-solid border-cement-200';

export const MONTH_WEEKDAY_HEADING_CLASS =
  'border-0 border-l border-solid border-cement-200 p-2 text-center';

export const MONTH_DAY_CELL_CLASS =
  'border-0 border-b border-l border-solid border-cement-200';

export const MONTH_DAY_CELL_TODAY_CLASS = 'border border-solid border-sage-500';
